import React, { useMemo } from 'react';

import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Alert, Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetRecentPriceSummaryJobs } from '~/hooks/apis/price-parity/useGetRecentPriceSummaryJobs';

import { formatDateSlashes, formatDateSlashesWithClock } from '~/services/TimeService';

import { PriceSummaryJob, PriceSummaryJobStatus } from '~/types/services/priceParrot';

import { parsePropertyNameFromTAURL } from '~/utils/priceParrot';
import { extractUsernameFromEmail } from '~/utils/stringUtils';

type Props = {
  onSelectScrape: (job: PriceSummaryJob) => void;
};

function deduplicateByProperty(jobs: Array<PriceSummaryJob>): Array<PriceSummaryJob> {
  const seen = new Map<string, PriceSummaryJob>();
  for (const job of jobs) {
    if (!seen.has(job.locationUrl)) {
      seen.set(job.locationUrl, job);
    }
  }
  return Array.from(seen.values());
}

const columns: Array<GridColDef<PriceSummaryJob>> = [
  {
    field: 'locationUrl',
    headerName: 'Property',
    flex: 3,
    renderCell: ({ row: { locationUrl } }) => {
      const name = parsePropertyNameFromTAURL(locationUrl);
      return (
        <Link href={locationUrl} target="_blank" rel="noopener noreferrer">
          {name ?? locationUrl}
        </Link>
      );
    },
  },
  { field: 'numberOfNights', headerName: 'Nights', flex: 0.6 },
  { field: 'currencyCode', headerName: 'Currency', flex: 0.7 },
  { field: 'countryCode', headerName: 'Country', flex: 0.7 },
  {
    field: 'startDate',
    headerName: 'Date Range',
    flex: 1.5,
    renderCell: ({ row }) => `${formatDateSlashes(row.startDate)} – ${formatDateSlashes(row.endDate)}`,
  },
  {
    field: 'createdAt',
    headerName: 'Scraped At',
    flex: 1.5,
    renderCell: ({ row: { createdAt, submittedByEmail } }) => (
      <Box>
        <Typography component="div" variant="body2">
          {formatDateSlashesWithClock(createdAt)}
        </Typography>
        {submittedByEmail && (
          <Typography component="div" variant="caption" color="text.secondary">
            by {extractUsernameFromEmail(submittedByEmail)}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    display: 'flex',
    renderCell: () => (
      <Tooltip title="Load report">
        <IconButton size="small" color="primary" tabIndex={-1}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
  },
];

export default function RecentScrapes({ onSelectScrape }: Props) {
  const { data: jobs = [], isLoading, error } = useGetRecentPriceSummaryJobs();

  const recentCompleted = useMemo(() => {
    const completed = jobs.filter((j) => j.status === PriceSummaryJobStatus.COMPLETED);
    return deduplicateByProperty(completed);
  }, [jobs]);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Could not load recent scrapes.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Recent scrapes
      </Typography>
      <DataGrid
        autoHeight
        columns={columns}
        rows={recentCompleted}
        loading={isLoading}
        getRowId={(row) => row.jobId}
        getRowHeight={() => 'auto'}
        onRowClick={({ row }) => onSelectScrape(row)}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnMenu
        sx={{
          cursor: 'pointer',
          '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
        }}
      />
    </Box>
  );
}
